import React from 'react';
import { ChevronDown16 } from 'ui/icons/aurora/16px/ChevronDown16';
import { TimeClock16 } from 'ui/icons/aurora/16px/TimeClock16';
import { SelectValueDisplayProps } from 'ui/selects/types';
import {
  TriggerWrapper,
  ValueDisplay,
  IconWrapper,
  Placeholder,
  SuffixWrapper,
} from './TimePicker.styles';

export type GetTimePickerTriggerArgs = {
  /** Whether to display the clock icon */
  hasClockIcon?: boolean;
  /** Whether to display the dropdown icon */
  hasChevronDownIcon?: boolean;
};

/**
 * Returns `TimePickerTrigger` component
 */
export const getTimePickerTrigger = ({
  hasClockIcon,
  hasChevronDownIcon,
}: GetTimePickerTriggerArgs) =>
  React.forwardRef(
    (
      {
        selectedOptions = [],
        placeholder,
        isOpen = false,
        toggleDropdown,
        isNonInteractive = false,
        isDisabled = false,
      }: SelectValueDisplayProps,
      ref,
    ) => {
      const [selectedOption] = selectedOptions;
      const { id, Suffix, label } = selectedOption || {};

      const displayValue = (
        <>
          {label}
          <SuffixWrapper $isOpen={isOpen}>{Suffix} </SuffixWrapper>
        </>
      );

      return (
        <TriggerWrapper
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          data-test="time-picker-toggle"
          data-values={selectedOptions.map(({ value }) => value)}
          type="button"
          $isOpen={isOpen}
          isDisabled={isDisabled}
          onClick={(e) => {
            if (!isNonInteractive) {
              e.stopPropagation();
              toggleDropdown();
            }
          }}
          ref={ref as React.ForwardedRef<HTMLButtonElement>}
        >
          {hasClockIcon && (
            <IconWrapper data-test={`${id || 'time-picker'}-toggle-clock`}>
              <TimeClock16 />
            </IconWrapper>
          )}

          <ValueDisplay as="div" data-test={`selected-option-${id || 'empty'}`} role={undefined}>
            {label ? displayValue : <Placeholder>{placeholder}</Placeholder>}
          </ValueDisplay>
          {hasChevronDownIcon && (
            <IconWrapper data-test={`${id || 'time-picker'}-toggle-down-chevron-down`}>
              <ChevronDown16 />
            </IconWrapper>
          )}
        </TriggerWrapper>
      );
    },
  );
